import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  SizeColumnsToFitGridStrategy,
} from "ag-grid-enterprise";
import {
  AgBarSeriesOptions,
  AgChartOptions,
  AgLineSeriesOptions,
} from "ag-charts-enterprise";
import useSidebar from "src/hooks/useSidebar";
import useReference from "src/hooks/useReference";
import useTheme from "src/hooks/useTheme";
import { AgCharts } from "ag-charts-react";
import BaseGrid from "../../BaseGrid";
import ValidationService from "src/services/ValidationService";
import useReport from "src/hooks/useReport";
import SearchCriteria from "src/components/SearchCriteria";
import LoadingComponent from "src/components/LoadingComponent";
import { TransactionSummaryModel } from "src/models/TransactionSummaryModel";
import { TransactionSummaryDetailModel } from "src/models/TransactionSummaryDetailModel";
import TransactionSummaryData from "./TransactionSummaryData";
import { downloadFile } from "src/utils/DownloadFile";

type ChartData = {
  paymentType: string;
  amount: number;
  transactions: number;
};

const TransactionSummary = () => {
  const { setIsOpen } = useSidebar();
  const { appSettings } = useReference();
  const { theme } = useTheme();
  const {
    reportInputs,
    setExceedsMaxDateRange,
    mapReportRequest,
    currencyFormatter,
    defaultColDef,
    getContextMenuItems,
  } = useReport();
  const [summaryDetail, setSummaryDetail] =
    useState<TransactionSummaryDetailModel>(
      {} as TransactionSummaryDetailModel
    );
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_View
      )
    ) {
      setIsDownload(false);
      setExceedsMaxDateRange(false);
      setGridReady(true);
      setSummaryDetail({} as TransactionSummaryDetailModel);
      setChartData([]);
      setDataSource(createServerSideDatasource());
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const handleDownloadReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_Download
      )
    ) {
      setIsDownload(true);
      setIsLoading(true);
      setExceedsMaxDateRange(false);
      ReportApi.exportTransactionSummary(mapReportRequest(null))
        .then((res) => {
          downloadFile(TransactionSummary.displayName, res.data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const [colDefs] = useState<ColDef<TransactionSummaryModel>[]>([
    { field: "paymentType", filter: true, cellStyle: { textAlign: "left" } },
    { field: "transactions", filter: true },
    {
      field: "amount",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "200px" },
      filter: true,
    },
  ]);

  const autoSizeStrategy = useMemo<SizeColumnsToFitGridStrategy>(() => {
    return {
      type: "fitGridWidth",
    };
  }, []);

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getTransactionSummary(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
        setSummaryDetail(result.summaryDetail);
        setChartData(result.rows);
        setOptions((prevOptions) => ({
          ...prevOptions,
          data: result.rows,
        }));
      })
      .catch(() => {
        params?.fail();
      });
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Payment Type Amounts & Counts",
    },
    // Data: Data to be displayed in the chart
    data: chartData as ChartData[],
    // Series: Defines which chart type and data to use
    series: [
      {
        type: "bar",
        xKey: "paymentType",
        yKey: "amount",
      } as AgBarSeriesOptions,
      {
        type: "line",
        xKey: "paymentType",
        yKey: "transactions",
      } as AgLineSeriesOptions,
    ],
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Transaction Summary</h5>
          {(reportInputs?.programId ?? 0) > 0 &&
            reportInputs?.fromDate &&
            reportInputs?.toDate && (
              <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
            )}
        </div>
        <div className="blocks">
          <SearchCriteria
            usesLocations={true}
            usesUsers={true}
            usesCampaigns={false}
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
          />
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-summary">
              <TransactionSummaryData summaryDetail={summaryDetail} />
            </div>
          )}
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
              dataSource={dataSource}
            />
          )
        )}
      </div>
    </>
  );
};
TransactionSummary.displayName = "TransactionSummary";

export default TransactionSummary;
